import React, { useCallback, useEffect, useRef, useState } from 'react';
import ListHeader from '../../common/list/ListHeader';
import TableReporting from './TableReporting';
import PeriodFilter from '../../common/filters/Period';
import StoreFilter from '../../common/filters/Store';
import ListActions from '../../common/list/ListActions';
import { useGetReportingQuery, useLazyGetReportingExportQuery } from '../../../services/api/repositories/EmailsRepository';

export default function ReportingSection() {

    const [dateRange, setDateRange] = useState<[Date|null, Date|null]>([null, null]);
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [store, setStore] = useState<string>('all');
    const storeRef = useRef<HTMLSelectElement>(null);

    const {data, isLoading, isFetching, refetch} = useGetReportingQuery(
        {
            date_start: dateStart,
            date_end: dateEnd,
            store: store,
        }
    );

    const [
        getReportingDownload,
        {
            data: exportData,
            isSuccess: isExportSuccess,
        },
    ] = useLazyGetReportingExportQuery();
    
    const hasSearchParametersChanged = useCallback(() => {
        if (storeRef.current?.value !== store) {
            return true;
        }

        if(dateRange[0] === null && dateStart !== '') {
            return true;
        }

        if(dateRange[1] === null && dateEnd !== '') {
            return true;
        }


        if(dateRange[0] !==null && dateStart !== `${dateRange[0].toLocaleDateString()} 00:00:00`){
            return true;
        }

        if(dateRange[1] !==null && dateEnd !== `${dateRange[1].toLocaleDateString()} 23:59:59`){
            return true;
        }

        return false;
    }, [dateRange, dateEnd, dateStart, store]);
    
    const handleSubmit = useCallback(() => {
        if(!hasSearchParametersChanged()) {
            refetch();
            return;
        }

        setStore(storeRef.current?.value || 'all');

        if(dateRange[0] !== null) {
            setDateStart(`${dateRange[0].toLocaleDateString()} 00:00:00`);
        } else {
            setDateStart('');
        }

        if(dateRange[1] !== null) {
            setDateEnd(`${dateRange[1].toLocaleDateString()} 23:59:59`);
        } else {
            setDateEnd('');
        }
    }, [refetch, dateRange, hasSearchParametersChanged]);

    const handleReset = useCallback(() => {
        storeRef.current!.value = 'all';
        setDateRange([null, null]);

        setStore('all');
        setDateStart('');
        setDateEnd('');
    }, []);

    const handleAction = useCallback(() => {
        getReportingDownload({date_start: dateStart, date_end: dateEnd, store: store});
    }, [getReportingDownload, dateStart, dateEnd, store]);

    useEffect(() => {
        if(isExportSuccess && exportData && exportData.data && exportData.data !== '' && exportData.name && exportData.name !== '') {
    
            const base64Data = exportData.data;
            const binaryString = atob(base64Data);
            const arrayBuffer = new ArrayBuffer(binaryString.length);
            const uint8Array = new Uint8Array(arrayBuffer);

            for (let i = 0; i < binaryString.length; i++) {
                    uint8Array[i] = binaryString.charCodeAt(i);
            }

            const blob = new Blob([uint8Array], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            if (
                blob.type !==
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ) {
                throw new Error(
                    "Le fichier reçu n'est pas un fichier Excel valide.",
                );
            }
              
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = exportData.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
    }
    }, [exportData, isExportSuccess]);

    return (
        <>
            <ListHeader 
                actions={(<ListActions
                    classAction='btn-export' 
                    title="Exporter les données"
                    textButton="Exporter"
                    handleAction={handleAction}
                    />)
                } 
                handleSubmit={handleSubmit}
                handleReset={handleReset}
            >
                <div className="filters">
                    <PeriodFilter tabIndex={1} dateRange={dateRange} setDateRange={setDateRange}/>
                    <StoreFilter tabIndex={2} ref={storeRef} />
                </div>
            </ListHeader>

            {(isLoading || isFetching) && <p>Chargement...</p>}
            
            {((!isLoading && !isFetching) && data !== undefined) && (
                <>
                    {("message" in data) && <p>{data.message}</p>}
                    {("total" in data) && 
                    (<>
                        <TableReporting data={data} />            
                    </>)}
                </>
                )
            }

            {((!isLoading && !isFetching) && data === undefined) && 
                <p>Une erreur a été rencontrée.</p>
            }
            
        </>
    );
}